import { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { font, Box, isMobile, useDialogContext } from '@lemonade-hq/cdk';
import { DialogBody, DialogCloseIcon } from '@lemonade-hq/nuis';
import { useAnalyticsContext } from '@lemonade-hq/nuiza';
import playIcnImg from '@/static/assets/home/paly-icn.svg';
import { DialogProvider } from '@lemonade-hq/cdk';

const DialogTrigger = styled.button`
  display: ${({ newIcon }: { newIcon?: boolean }) => (newIcon ? 'block' : 'flex')};
  align-items: center;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: ${({ newIcon, theme }) => (newIcon ? theme.colors.boulder : theme.colors.primary)};
  ${font('main', { fontSize: '1.6rem', lineHeight: 1.3 })};

  &:hover {
    text-decoration: underline;
  }

  &:focus {
    outline: 0;
  }
`;

const PlayIcn = styled.img`
  width: 22px;
  height: 22px;
`;

const CloseIcn = styled(DialogCloseIcon)`
  top: -30px;
  right: -30px;
  width: 40px;
  height: 40px;
`;

const VimeoContent = styled(DialogBody)`
  animation: none;
  background-color: transparent;
  padding: 0 !important;
`;

const VimeoIframe = styled.iframe`
  background: black;
`;

interface VimeoLightboxProps {
  text: string;
  videoId: number;
  event?: string;
  gtmId: string;
}
const VimeoLightbox = ({ text, videoId, event, gtmId }: VimeoLightboxProps) => {
  const { trackEvent } = useAnalyticsContext();
  const { openDialog, closeDialog } = useDialogContext();
  const [{ width, height }, setDimensions] = useState({ width: 0, height: 0 });

  const calculateVideoDimensions = (): void => {
    const widthFactor = isMobile() ? 0.85 : 0.6;
    const width = window.innerWidth * widthFactor;
    const height = (width * 9) / 16 - 1;

    setDimensions({ width, height });
  };
  // biome-ignore lint/correctness/useExhaustiveDependencies: no need of dependencies
  useEffect(() => {
    calculateVideoDimensions();
    window.addEventListener('resize', calculateVideoDimensions);

    return () => {
      window.removeEventListener('resize', calculateVideoDimensions);
    };
  }, []);

  const onOpenDialog = useCallback(() => {
    openDialog(
      <VimeoContent>
        <CloseIcn onClick={closeDialog} />
        <VimeoIframe
          width={width}
          height={height}
          src={`https://player.vimeo.com/video/${videoId}?autoplay=true`}
          frameBorder="0"
          allow="autoplay; encrypted-media"
          allowFullScreen
          title="Video Review of Lemonade Claims"
        />
      </VimeoContent>,
    );
  }, [openDialog, width, height, videoId, closeDialog]);

  function handleClick(): void {
    trackEvent('home.lemonade_app_video.clicked');
    onOpenDialog();
  }

  return (
    <DialogProvider>
      <DialogTrigger onClick={handleClick} data-event={event} id={gtmId}>
        <PlayIcn alt="" src={playIcnImg.src} />
        <Box ml={10}>{text}</Box>
      </DialogTrigger>
    </DialogProvider>
  );
};

export default VimeoLightbox;
